import React, { useState, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Divider, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Tooltip, 
  CircularProgress,
  Collapse,
  Paper,
  Button
} from '@mui/material';
import { 
  FileText, 
  Image, 
  File, 
  FileArchive, 
  ChevronRight, 
  ChevronDown, 
  Download, 
  Trash2,
  Database,
  Upload,
  AlertCircle,
  Plus
} from 'lucide-react';

const KnowledgePanel = ({ 
  files, 
  onDeleteFile, 
  isLoading, 
  darkMode,
  onUploadFile,
  uploadProgress
}) => {
  const [expanded, setExpanded] = useState(true);
  const [uploadHover, setUploadHover] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  // Get appropriate icon based on file extension
  const getFileIcon = (filename) => {
    const extension = (filename || '').split('.').pop().toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return <FileText size={20} color={darkMode ? "#90CAF9" : "#1976d2"} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image size={20} color={darkMode ? "#AED581" : "#689F38"} />;
      case 'zip':
      case 'rar':
      case '7z':
        return <FileArchive size={20} color={darkMode ? "#FFD54F" : "#FFA000"} />;
      case 'doc':
      case 'docx':
      case 'txt':
        return <FileText size={20} color={darkMode ? "#90CAF9" : "#1976d2"} />;
      default:
        return <File size={20} color={darkMode ? "#E0E0E0" : "#757575"} />;
    }
  };

  // Map backend statuses to user-friendly labels
  const mapStatusLabel = (status) => {
    const s = (status || '').toLowerCase();
    if (['ready', 'completed', 'upload_complete'].includes(s)) return 'Ready';
    if (['failed', 'error'].includes(s)) return 'Error';
    if (['processing', 'extracting', 'extracted', 'embedding', 'embedded', 'summary', 'summary_generated', 'pending'].includes(s)) return 'Processing';
    return s || 'Processing';
  };

  // Get status icon/indicator based on process status
  const getStatusIndicator = (status) => {
    const s = (status || '').toLowerCase();
    if (['failed', 'error'].includes(s)) {
        return <AlertCircle size={16} color="#f44336" />;
    }
    if (['ready', 'completed', 'upload_complete'].includes(s)) {
        return null;
    }
    // Show spinner for any in-progress state
    return <CircularProgress size={16} />;
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length > 0) {
      // Trigger upload for each file
      newFiles.forEach(file => {
        onUploadFile(file);
      });
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Toggle panel expansion
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Open file input dialog
  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropAreaRef.current && !dropAreaRef.current.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      newFiles.forEach(file => {
        onUploadFile(file);
      });
    }
  }, [onUploadFile]);

  return (
    <Box>
      {/* Panel Title */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          Knowledge Files
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload documents to enhance the assistant's knowledge
        </Typography>
        <Divider />
      </Box>

      <Paper
        elevation={2}
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: '10px',
          mb: 2,
          bgcolor: darkMode ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.95)',
          border: '1px solid',
          borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: darkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
          }
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
            },
          }}
          onClick={toggleExpanded}
        >
          <IconButton
            size="small"
            sx={{ 
              mr: 1, 
              transition: 'transform 0.3s',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)'
            }}
          >
            <ChevronDown size={20} />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Database size={18} style={{ marginRight: 8 }} color={darkMode ? "#64B5F6" : "#1976d2"} />
            <Typography variant="subtitle2" fontWeight="medium">
              Uploaded Files
            </Typography>
            {files?.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', px: 1, py: 0.2, borderRadius: '10px' }}
              >
                {files.length}
              </Typography>
            )}
          </Box>

          {/* Hidden file input */}
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          
          {/* Upload button */}
          <Tooltip title="Upload file">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openFileSelector();
              }}
              onMouseEnter={() => setUploadHover(true)}
              onMouseLeave={() => setUploadHover(false)}
              sx={{
                color: uploadHover ? 'primary.main' : 'inherit',
                bgcolor: uploadHover ? (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)') : 'transparent',
                '&:hover': {
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s'
              }}
            >
              <Upload size={18} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Files list - collapsible */}
        <Collapse in={expanded} timeout="auto">
          <Divider />
          
          {/* Upload progress indicator */}
          {uploadProgress && (
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                bgcolor: darkMode ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)',
                borderLeft: '3px solid',
                borderColor: 'primary.main'
              }}
            >
              <CircularProgress size={16} sx={{ mr: 1.5 }} />
              <Typography variant="body2">
                Uploading {uploadProgress.filename}...
              </Typography>
            </Box>
          )}
          
          {/* Drag & Drop Area - Only show when no files or explicitly needed */}
          {(!files || files.length === 0 || isDragging) && !uploadProgress && (
            <Box
              ref={dropAreaRef}
              sx={{
                p: 3,
                m: 2,
                textAlign: 'center',
                color: 'text.secondary',
                border: '2px dashed',
                borderColor: isDragging 
                  ? 'primary.main' 
                  : darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                borderRadius: '8px',
                bgcolor: isDragging 
                  ? (darkMode ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)')
                  : (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)'),
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: darkMode ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)',
                }
              }}
              onClick={openFileSelector}
            >
              <Upload size={24} style={{ marginBottom: 8 }} />
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                {isDragging ? 'Drop files here' : 'Drag & drop files here'}
              </Typography>
              <Typography variant="caption">
                or <span style={{ color: '#1976d2', textDecoration: 'underline' }}>click to browse</span>
              </Typography>
            </Box>
          )}

          {/* Files list */}
          {files && files.length > 0 && (
            <>
              <List dense sx={{ pt: 0 }}>
                {files.map((file) => (
                  <ListItem
                    key={file.id}
                    sx={{
                      py: 0.75,
                      px: 2,
                      '&:hover': {
                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      },
                      borderLeft: file.process_status === 'error' ? '3px solid #f44336' : 
                        file.process_status === 'processing' ? '3px solid #2196f3' : 'none',
                      pl: file.process_status === 'error' || file.process_status === 'processing' ? 1.7 : 2,
                    }}
                    secondaryAction={
                      <Box sx={{ display: 'flex' }}>
                        {/* Status indicator */}
                        {getStatusIndicator(file.process_status)}
                        
                        {/* Download button */}
                        <Tooltip title="Download">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => window.open(file.file, '_blank')}
                            sx={{ ml: 1 }}
                          >
                            <Download size={16} />
                          </IconButton>
                        </Tooltip>
                        
                        {/* Delete button */}
                        <Tooltip title="Remove">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => onDeleteFile(file.id)}
                            disabled={isLoading}
                            sx={{ 
                              ml: 0.5,
                              '&:hover': {
                                color: 'error.main',
                                bgcolor: darkMode ? 'rgba(244,67,54,0.1)' : 'rgba(244,67,54,0.05)'
                              }
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getFileIcon(file.filename)}
                    </ListItemIcon>
                    <ListItemText
                      primary={file.filename}
                      primaryTypographyProps={{
                        variant: "body2",
                        noWrap: true,
                        title: file.filename,
                        fontWeight: 'medium'
                      }}
                      secondary={mapStatusLabel(file.process_status)}
                      secondaryTypographyProps={{
                        variant: "caption",
                        color: ['failed','error'].includes((file.process_status||'').toLowerCase()) ? 'error' : 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              {/* Add files button at the bottom */}
              <Box sx={{ 
                p: 1, 
                display: 'flex', 
                justifyContent: 'center',
                borderTop: '1px solid',
                borderColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
              }}>
                <Button
                  startIcon={<Plus size={16} />}
                  onClick={openFileSelector}
                  variant="text"
                  size="small"
                  sx={{ 
                    textTransform: 'none',
                    borderRadius: '20px',
                    px: 2
                  }}
                >
                  Add more files
                </Button>
              </Box>
            </>
          )}
        </Collapse>
      </Paper>

      {/* Additional help text */}
      <Box sx={{ mt: 3, p: 2, bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
          What you can upload
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add documents like PDFs, Word files, text files, and spreadsheets to enhance the assistant's knowledge. The uploaded files will be processed and made available for the assistant to reference during the conversation.
        </Typography>
      </Box>
    </Box>
  );
};

export default KnowledgePanel; 